import { createSlice } from '@reduxjs/toolkit'
import { REDUX_STATUS_IDLE } from 'src/consumer/constants'

const initialState = {
  emrSections: {
    state: REDUX_STATUS_IDLE,
    data: {},
    error: null,
  },
  appointmentCalendarSettings: {
    state: REDUX_STATUS_IDLE,
    data: null,
    error: null,
    // API signature the cached data was fetched with — the support portal loads
    // other practitioners' settings into this same slice, so a cache hit is only
    // valid when the signature matches
    fetchedFor: null,
  },
  prescriptionViewSettings: {
    state: REDUX_STATUS_IDLE,
    data: null,
    error: null,
  },
  drugAdviceTypes: {
    state: REDUX_STATUS_IDLE,
    data: [],
    error: null,
  },
  drugAdviceDosage: {
    state: REDUX_STATUS_IDLE,
    data: [],
    error: null,
  },
  drugAdviceEntities: {},
}

const prescriptionSlice = createSlice({
  name: 'prescriptionStore',
  initialState: initialState,
  reducers: {
    setEmrSectionsData(state, action) {
      state.emrSections.data = action.payload
    },
    setEmrSectionsError(state, action) {
      state.emrSections.error = action.payload
    },
    setEmrSectionsState(state, action) {
      state.emrSections.state = action.payload
    },

    setPrescriptionViewSettingsData(state, action) {
      state.prescriptionViewSettings.data = action.payload
    },
    setPrescriptionViewSettingsError(state, action) {
      state.prescriptionViewSettings.error = action.payload
    },
    setPrescriptionViewSettingsState(state, action) {
      state.prescriptionViewSettings.state = action.payload
    },

    setAppointmentCalendarSettingsData(state, action) {
      state.appointmentCalendarSettings.data = action.payload
    },
    setAppointmentCalendarSettingsError(state, action) {
      state.appointmentCalendarSettings.error = action.payload
    },
    setAppointmentCalendarSettingsStatus(state, action) {
      state.appointmentCalendarSettings.status = action.payload
    },
    setAppointmentCalendarSettingsFetchedFor(state, action) {
      state.appointmentCalendarSettings.fetchedFor = action.payload
    },

    setDrugAdviceTypesData(state, action) {
      state.drugAdviceTypes.data = action.payload
    },
    setDrugAdviceTypesError(state, action) {
      state.drugAdviceTypes.error = action.payload
    },
    setDrugAdviceTypesState(state, action) {
      state.drugAdviceTypes.state = action.payload
    },

    setEntityData(state, action) {
      const { entity, data } = action.payload
      state.drugAdviceEntities[entity] = {
        ...(state.drugAdviceEntities[entity] || {}),
        data,
      }
    },
    setEntityError(state, action) {
      const { entity, error } = action.payload
      state.drugAdviceEntities[entity] = {
        ...(state.drugAdviceEntities[entity] || {}),
        error,
      }
    },
    setEntityState(state, action) {
      const { entity, status } = action.payload
      state.drugAdviceEntities[entity] = {
        ...(state.drugAdviceEntities[entity] || {}),
        state: status,
      }
    },
  },
})

function selectPrescriptionStore(state) {
  return state.prescriptionStore
}

export const {
  setEmrSectionsData,
  setEmrSectionsError,
  setEmrSectionsState,

  setPrescriptionViewSettingsState,
  setPrescriptionViewSettingsError,
  setPrescriptionViewSettingsData,

  setDrugAdviceTypesData,
  setDrugAdviceTypesError,
  setDrugAdviceTypesState,

  setAppointmentCalendarSettingsData,
  setAppointmentCalendarSettingsError,
  setAppointmentCalendarSettingsStatus,
  setAppointmentCalendarSettingsFetchedFor,

  setEntityData,
  setEntityError,
  setEntityState,
} = prescriptionSlice.actions

export const prescriptionReducer = prescriptionSlice.reducer

export const selectEmrSectionsData = (state) => selectPrescriptionStore(state).emrSections
export const selectPrescriptionViewSettingsData = (state) =>
  selectPrescriptionStore(state).prescriptionViewSettings
export const selectDrugAdviceTypesData = (state) => selectPrescriptionStore(state).drugAdviceTypes
export const selectAppointmentCalendarSettings = (state, withFallback = true) =>
  withFallback
    ? selectPrescriptionStore(state).appointmentCalendarSettings || {
        showAppointmentNumberHorizontally: false,
        showPatientVisits: true,
        showPatientAgeAndGender: true,
        showWaitingTime: true,
      }
    : selectPrescriptionStore(state).appointmentCalendarSettings

export const selectDrugAdviceEntity = (entity) => (state) => {
  return (
    state.prescriptionStore.drugAdviceEntities?.[entity] || {
      state: 'idle',
      data: [],
      error: null,
    }
  )
}
